// Single-column sort: the toggle cycle and a typed, stable comparison.

import { cellValue, isNumber, parseIsoDate, productLocale } from '../format'

// A non-default column cycles ascending -> descending -> the default sort; the default column alternates.
export const nextSort = (current, key, defaultSort) => {
  if (current.key !== key) return { key, direction: 'ascending' }
  if (key === defaultSort.key) {
    return {
      key,
      direction: current.direction === 'ascending' ? 'descending' : 'ascending',
    }
  }
  return current.direction === 'ascending'
    ? { key, direction: 'descending' }
    : { ...defaultSort }
}

// A user sort on a column that is gone or hidden gives way to the default sort, which stays in effect even
// while its own column is hidden.
export const reconcileSort = (sort, defaultSort, { keys, hidden = [] }) => {
  if (!sort || sort.key === defaultSort.key) return sort
  const gone = (keys && !keys.includes(sort.key)) || hidden.includes(sort.key)
  return gone ? { ...defaultSort } : sort
}

export const isMissing = value =>
  value === null ||
  value === undefined ||
  value === '' ||
  (typeof value === 'number' && !Number.isFinite(value))

const typedValue = (column, value) => {
  if (column.sortOrder) {
    const rank = column.sortOrder.indexOf(value)
    return rank === -1 ? null : rank
  }
  if (column.type === 'numeric') return isNumber(value) ? value : null
  if (column.type === 'date') return parseIsoDate(value)
  return isMissing(value) ? null : String(value)
}

export const sortRows = (rows, sort, columns, locale) => {
  const column = sort && columns.find(c => c.key === sort.key)
  if (!column) return rows.slice()
  const collator = new Intl.Collator(productLocale(locale), {
    sensitivity: 'base',
    numeric: true,
  })
  const direction = sort.direction === 'descending' ? -1 : 1
  return rows
    .map((row, index) => ({
      row,
      index,
      value: typedValue(column, cellValue(column, row)),
    }))
    .sort((a, b) => {
      // missing or invalid values sort last in both directions
      const aMissing = a.value === null
      const bMissing = b.value === null
      if (aMissing || bMissing)
        return aMissing === bMissing ? a.index - b.index : aMissing ? 1 : -1
      const order =
        typeof a.value === 'string'
          ? collator.compare(a.value, b.value)
          : a.value - b.value
      // equal values keep source order
      return order === 0 ? a.index - b.index : order * direction
    })
    .map(item => item.row)
}
